import { createServerFn } from "@tanstack/react-start";
import { sendTransactionalEmail } from "./sendTransactionalEmail.server";
import { CONSULT_EXPERTS, CONSULT_SITE_URL, randomConsultToken, type ConsultExpertKey } from "./consultFollowup.server";

export interface CreateConsultFollowupInput {
  expertKey: string;
  track?: string | null;
  entry?: string | null;
  memberEmail?: string | null;
  memberName?: string | null;
  memberContext?: Record<string, unknown> | null;
}

export interface CreateConsultFollowupResult {
  success: true;
  id: string;
  expertToken: string;
  memberToken: string;
}

// Public/unauthenticated: called from HomieChat right after a consult booking
// (anonymous or signed-in members both use this flow); mirrors the original
// edge function which had no auth gate and only optionally read the signed-in
// user off the Authorization header.
export const createConsultFollowup = createServerFn({ method: "POST" })
  .inputValidator((d: CreateConsultFollowupInput) => d)
  .handler(async ({ data }): Promise<CreateConsultFollowupResult> => {
    const { expertKey, track, entry, memberEmail, memberName, memberContext } = data;

    if (!expertKey || !(expertKey in CONSULT_EXPERTS)) {
      throw new Error("Invalid expertKey");
    }

    const expert = CONSULT_EXPERTS[expertKey as ConsultExpertKey];
    const expertToken = randomConsultToken();
    const memberToken = randomConsultToken();

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: row, error: insertErr } = await supabaseAdmin
      .from("consult_followups")
      .insert({
        expert_key: expertKey,
        journey_track: track ?? null,
        consult_entry: entry ?? null,
        expert_token: expertToken,
        member_token: memberToken,
        member_context: (memberContext ? JSON.parse(JSON.stringify(memberContext)) : null) as never,
      })
      .select("id")
      .single();

    if (insertErr || !row) {
      console.error("Failed to insert consult_followup", insertErr);
      throw new Error("Failed to record consult");
    }

    const expertLink = `${CONSULT_SITE_URL}/expert-followup/${expertToken}`;
    const memberLink = `${CONSULT_SITE_URL}/member-followup/${memberToken}`;

    try {
      await sendTransactionalEmail({
        templateName: "expert-consult-followup",
        recipientEmail: expert.email,
        idempotencyKey: `expert-consult-followup:${row.id}`,
        templateData: {
          expertName: expert.name,
          memberName: memberName ?? memberEmail ?? "a Home Approach member",
          track: track ?? null,
          followupUrl: expertLink,
        },
      });
    } catch (e) {
      console.error("email send failed (expert-consult-followup)", e);
    }

    if (memberEmail) {
      try {
        await sendTransactionalEmail({
          templateName: "member-consult-followup",
          recipientEmail: memberEmail,
          idempotencyKey: `member-consult-followup:${row.id}`,
          templateData: {
            memberName: memberName ?? "",
            expertName: expert.name,
            expertCompany: expert.company,
            followupUrl: memberLink,
          },
        });
      } catch (e) {
        console.error("email send failed (member-consult-followup)", e);
      }
    }

    return { success: true, id: row.id as string, expertToken, memberToken };
  });

export interface ConsultFollowupContext {
  id: string;
  expert_key: string;
  journey_track: string | null;
  expert_submitted_at: string | null;
  member_submitted_at: string | null;
}

export interface GetConsultFollowupContextInput {
  token: string;
  role: "expert" | "member";
}

export interface GetConsultFollowupContextResult {
  followup: ConsultFollowupContext;
}

// Public token-authenticated lookup for the expert/member follow-up pages.
export const getConsultFollowupContext = createServerFn({ method: "POST" })
  .inputValidator((d: GetConsultFollowupContextInput) => d)
  .handler(async ({ data }): Promise<GetConsultFollowupContextResult> => {
    const { token, role } = data;
    if (!token || (role !== "expert" && role !== "member")) {
      throw new Error("Bad request");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const col = role === "expert" ? "expert_token" : "member_token";
    const { data: row, error } = await supabaseAdmin
      .from("consult_followups")
      .select("id, expert_key, journey_track, expert_submitted_at, member_submitted_at")
      .eq(col, token)
      .maybeSingle();

    if (error || !row) {
      throw new Error("Invalid or expired link");
    }

    return { followup: row as ConsultFollowupContext };
  });

export interface SubmitConsultFollowupInput {
  token: string;
  role: "expert" | "member";
  payload: Record<string, unknown>;
}

export interface SubmitConsultFollowupResult {
  success: true;
}

// Public token-authenticated submission for the expert/member follow-up pages.
export const submitConsultFollowup = createServerFn({ method: "POST" })
  .inputValidator((d: SubmitConsultFollowupInput) => d)
  .handler(async ({ data }): Promise<SubmitConsultFollowupResult> => {
    const { token, role, payload } = data;
    if (!token || (role !== "expert" && role !== "member") || !payload || typeof payload !== "object") {
      throw new Error("Missing token/role/payload");
    }

    if (JSON.stringify(payload).length > 20000) {
      throw new Error("Payload too large");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const tokenCol = role === "expert" ? "expert_token" : "member_token";
    const respCol = role === "expert" ? "expert_response" : "member_response";
    const stampCol = role === "expert" ? "expert_submitted_at" : "member_submitted_at";

    const { data: row, error: lookupErr } = await supabaseAdmin
      .from("consult_followups")
      .select(`id, ${stampCol}`)
      .eq(tokenCol, token)
      .maybeSingle();

    if (lookupErr || !row) {
      throw new Error("Invalid link");
    }

    if ((row as Record<string, unknown>)[stampCol]) {
      throw new Error("Already submitted");
    }

    const { error: updateErr } = await supabaseAdmin
      .from("consult_followups")
      .update({
        [respCol]: JSON.parse(JSON.stringify(payload)),
        [stampCol]: new Date().toISOString(),
      } as never)
      .eq("id", (row as { id: string }).id);

    if (updateErr) {
      console.error("submitConsultFollowup update failed", updateErr);
      throw new Error("Failed to save");
    }

    return { success: true };
  });
